import { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Card, Select, showSnackbar, Button, Checkbox, FormGroup } from "@cofano/cck";
import { Alert, Box, Grid2 as Grid, Stack } from "@mui/material";
import { CloudDownload, CloudUpload } from "@mui/icons-material";

import { hasPluginPermission } from "utils";

import LocationTypeSelect from "view/locations/components/selects/LocationTypeSelect";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";

import { LocationType, Scheduler } from "__generated__/enums";
import { UpdateTerminalsDocument } from "__generated__/view/locations/graphql/location.mutations.generated";
import { GetPreloadDocument, GetPreloadLocationDocument } from "__generated__/view/admin/graphql/admin.queries.generated";
import { BulkPersistAllDataDocument, RequestMasterdataSyncDocument, SendMasterdataSyncDocument, TriggerSchedulersDocument } from "__generated__/view/admin/graphql/admin.mutations.generated";
import { RecalculateNextCallAllShipsDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";

const AdminTrigger = () => {
	return (
		<Grid container spacing={2}>
			<Grid size={4}>
				<Stack spacing={2}>
					<RoutePreload />
					<RoutePreload2 />
					<MasterdataSync />
				</Stack>
			</Grid>

			<Grid size={4}>
				<Stack spacing={2}>
					<TerminalSynchronization />
					<Encryption />
					<CalculateNextCall />
				</Stack>
			</Grid>

			<Grid size={4}>
				<Stack spacing={2}>
					<Schedulers />
				</Stack>
			</Grid>
		</Grid>
	);
};

const subtitleBox = (id: string) => (
	<Box sx={{ textTransform: "none", whiteSpace: "normal" }}>
		{intl.formatMessage({ id })}
	</Box>
);

const RoutePreload = () => {

	const [ location, setLocation ] = useState<LocationSelectOption | null>(null);
	const [ override, setOverride ] = useState(false);

	const [ getPreloadLocation, { loading } ] = useLazyQuery(GetPreloadLocationDocument);

	const trigger = () => {
		if (!location) {
			return;
		}

		getPreloadLocation({
			variables: {
				locationId: location.id,
				override,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "ROUTE_PRELOAD" })}
			subtitle={subtitleBox("ADMIN_TRIGGER_CACHE_ALL_ROUTES")}>
			<FormGroup label={""}>
				<LocationSelect value={location} onChange={setLocation} />
			</FormGroup>

			<FormGroup label={""}>
				<Checkbox
					checked={override}
					onChange={(event) => setOverride(event.target.checked)}
					label={intl.formatMessage({ id: "OVERRIDE" })} />
			</FormGroup>

			<Button variant="outlined"
				size="small"
				disabled={!location}
				onClick={trigger}
				loading={loading}>
				{intl.formatMessage({ id: "PRE_LOAD_ROUTES" })}
			</Button>
		</Card>
	);
};

const RoutePreload2 = () => {

	const [ locationType, setLocationType ] = useState<LocationType>(LocationType.Terminal);
	const [ override, setOverride ] = useState(false);

	const [ getPreload, { loading } ] = useLazyQuery(GetPreloadDocument);

	const trigger = () => {
		getPreload({
			variables: {
				type: locationType,
				override,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "ROUTE_PRELOAD" })}
			subtitle={subtitleBox("ADMIN_TRIGGER_CACHE_ALL_ROUTES")}>
			<FormGroup label={""}>
				<LocationTypeSelect
					value={locationType}
					onChange={(option) => option && setLocationType(option)}
					clearable={false} />
			</FormGroup>

			<FormGroup label={""}>
				<Checkbox checked={override} onChange={(event) => setOverride(event.target.checked)} label={intl.formatMessage({ id: "OVERRIDE" })} />
			</FormGroup>

			<Button variant="outlined"
				size="small"
				onClick={trigger}
				loading={loading}>
				{intl.formatMessage({ id: "PRE_LOAD_ROUTES" })}
			</Button>
		</Card>
	);
};

const MasterdataSync = () => {
	const session = useStoreState(store => store.session);

	const canSend = hasPluginPermission(session, "publishMasterdataSyncEvents");
	const canReceive = hasPluginPermission(session, "receiveMasterdataSyncEvents");

	const [ sendMasterdataSync, { loading: sendLoading } ] = useMutation(SendMasterdataSyncDocument);
	const [ requestMasterdataSync, { loading: requestLoading } ] = useMutation(RequestMasterdataSyncDocument);

	const send = () => {
		sendMasterdataSync().then(({ data }) => {
			showSnackbar(`Queued ${data?.sendMasterdataSync} entities for synchronization`, { variant: "success" });
		});
	};

	const request = () => {
		requestMasterdataSync().then(({ data }) => {
			showSnackbar(`Received and synced ${data?.requestMasterdataSync} entities`, { variant: "success" });
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "MASTER_DATA_SYNC" })}
			subtitle={subtitleBox("MASTER_DATA_SYNCHRONIZE")}>
			<Stack spacing={2}>
				<Alert severity="warning" sx={{ fontSize: 13, alignItems: "center", py: 0.5 }}>
					{intl.formatMessage({ id: "DATA_SYNC" })}
				</Alert>

				<Stack direction="row" spacing={1}>
					<Button
						variant="outlined"
						size="small"
						onClick={send}
						loading={sendLoading}
						disabled={!canSend}
						icon={<CloudUpload />}>
						{intl.formatMessage({ id: "MASTER_DATA_SEND" })}
					</Button>

					<Button
						variant="outlined"
						size="small"
						onClick={request}
						loading={requestLoading}
						disabled={!canReceive}
						icon={<CloudDownload />}>
						{intl.formatMessage({ id: "MASTER_DATA_REQUEST" })}
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};

const TerminalSynchronization = () => {

	const [ updateTerminals, { loading } ] = useMutation(UpdateTerminalsDocument);

	const synchronizeLastUpdate = () => {
		updateTerminals().then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		});
	};

	const synchronizeAll = () => {
		updateTerminals({
			variables: {
				fullSynchronize: true,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "TERMINAL_SYNCHRONIZATION" })}
			subtitle={subtitleBox("SYNCHRONIZE TERMINALS ADN UNLOCODES")}>
			<Stack direction="row" spacing={1}>
				<Button
					variant="outlined"
					size="small"
					onClick={synchronizeLastUpdate}
					loading={loading}>
					{intl.formatMessage({ id: "SYNCHRONIZE_LAST_UPDATE" })}
				</Button>

				<Button
					variant="outlined"
					size="small"
					onClick={synchronizeAll}
					loading={loading}>
					{intl.formatMessage({ id: "SYNCHRONIZE_ALL" })}
				</Button>
			</Stack>
		</Card>
	);
};

const Encryption = () => {

	const ENCRYPTION_OPTIONS = [
		{ id: "ENTITY_TABLE", name: intl.formatMessage({ id: "CHOOSE_ENTITY_TABLE" }) },
		{ id: "CARGO", name: intl.formatMessage({ id: "CARGO" }) },
	];

	const [ bulkPersistAllData, { loading } ] = useMutation(BulkPersistAllDataDocument);
	const [ encryption, setEncryption ] = useState(ENCRYPTION_OPTIONS[0]);

	const trigger = () => {
		bulkPersistAllData({
			variables: {
				entity: encryption.id === "CARGO" ? "cargo" : "",
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "ENCRYPTION_TRIGGER_DATA" })}
			subtitle={subtitleBox("RECORDS_ENCRYPT_DE_ENCRYPT_PERSIST")}>
			<Alert severity="warning" sx={{ fontSize: 13, alignItems: "center", py: 0.5 }}>
				{intl.formatMessage({ id: "RECORDS_PERSIST_CONFIRM" })}
			</Alert>

			<FormGroup label={""}>
				<Select
					onChange={(option) => option && setEncryption(option)}
					fetchOptions={ENCRYPTION_OPTIONS}
					getOptionLabel={option => option.name}
					clearable={false}
					value={encryption} />
			</FormGroup>

			<Button
				variant="outlined"
				size="small"
				onClick={trigger}
				loading={loading}>
				{intl.formatMessage({ id: "ALL_RECORDS_PERSIST" })}
			</Button>
		</Card>
	);
};


const CalculateNextCall = () => {

	const [ recalculate, { loading } ] = useMutation(RecalculateNextCallAllShipsDocument);

	const synchronizeLastUpdate = () => {
		recalculate().then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "NEXT_CALLS_RECALCULATE" })}
			subtitle={subtitleBox("ALL_NEXT_CALLS_NOT_DEPRECATED_SHIP_RECALCULATE")}>
			<Alert severity="warning" sx={{ fontSize: 13, alignItems: "center", py: 0.5 }}>
				{intl.formatMessage({ id: "ADMIN_TRIGGER_WARNING" })}
			</Alert>

			<p>{intl.formatMessage({ id: "PROCESS_LOG_CHECK" })}</p>

			<Button
				variant="outlined"
				size="small"
				onClick={synchronizeLastUpdate}
				loading={loading}>
				{intl.formatMessage({ id: "NEXT_CALL_CALCULATE" })}
			</Button>
		</Card>
	);
};

type SchedulerError = Record<string, string>;

const Schedulers = () => {
	const [ triggerSchedulers, { loading } ] = useMutation(TriggerSchedulersDocument);
	const [ schedulers, setSchedulers ] = useState<Scheduler[]>([]);
	const [ errors, setErrors ] = useState<SchedulerError[]>([]);

	const { settings } = useStoreState(store => store.session);

	const SCHEDULERS: Record<Scheduler, boolean | undefined> = {
		[Scheduler.Cargo]: settings?.plugins.orders,
		[Scheduler.Contract]: settings?.plugins.contracts && settings?.settings.contractExpirerDays !== "0",
		[Scheduler.Voyage]: settings?.plugins.voyages,
		[Scheduler.Call]: settings?.plugins.voyages,
		[Scheduler.CallExpected]: true,
		[Scheduler.ScopeIn]: settings?.plugins.scopeInExpected,
		[Scheduler.TerminalSync]: settings?.plugins.downloadTankTerminals || settings?.plugins.downloadContainerTerminals || settings?.plugins.downloadUnlocodes,
		[Scheduler.SluiceSync]: settings?.plugins.downloadSluices,
		[Scheduler.DocumentExpire]: true,
		[Scheduler.ActiveFleet]: settings?.plugins.fleets && settings?.plugins.activeFleets,
		[Scheduler.Currency]: settings?.plugins.contracts,
		[Scheduler.CallLaLoList]: true,
		[Scheduler.DataCleanup]: true,
		[Scheduler.LocationCacheSync]: true,
		[Scheduler.NotificationQueue]: true,
		[Scheduler.RentDeclaration]: settings?.plugins.automaticRentDeclarations,
	};

	const has = (scheduler: Scheduler) => schedulers.includes(scheduler);

	const handle = (scheduler: Scheduler) => {
		if (schedulers.includes(scheduler)) {
			setSchedulers(schedulers.filter(s => s !== scheduler));
		} else {
			setSchedulers(schedulers.concat(scheduler));
		}
	};

	const trigger = () => {
		triggerSchedulers({
			variables: {
				schedulers,
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "OK" }), { variant: "success" });
		}).catch((err: SchedulerError[]) => {
			setErrors(err);
		});
	};

	return (
		<Card
			title={intl.formatMessage({ id: "SCHEDULERS" })}
			subtitle={subtitleBox("SCHEDULERS_TRIGGER_MANUALLY")}>
			<FormGroup label={""}>
				<Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 0.75 }}>
					{(Object.keys(SCHEDULERS) as Scheduler[]).map(scheduler => SCHEDULERS[scheduler] && (
						<Box key={scheduler} sx={{ flex: "0 0 auto" }}>
							<Checkbox
								checked={has(scheduler)}
								onChange={() => handle(scheduler)}
								label={scheduler} />
						</Box>
					))}
				</Box>
			</FormGroup>

			{errors.length > 0 && (
				<FormGroup label={""}>
					<Stack spacing={1}>
						{errors.map((err, i) => {
							const key = Object.keys(err)[0];
							return <Alert key={i} severity="error" sx={{ fontSize: 13, alignItems: "center", py: 0.5 }}>{`${key} - ${err[key]}`}</Alert>;
						})}
					</Stack>
				</FormGroup>
			)}

			<FormGroup label={""}>
				<Stack spacing={1}>
					<Box sx={{ alignSelf: "flex-start" }}>
						<Button variant="outlined"
							size="small"
							disabled={schedulers.length === 0}
							onClick={trigger}
							loading={loading}>
							{intl.formatMessage({ id: "SELECTED_SCHEDULERS_TRIGGER" })}
						</Button>
					</Box>
					<div>{intl.formatMessage({ id: "SCHEDULERS_TIMEOUT_NOTE" })}</div>
				</Stack>
			</FormGroup>
		</Card>
	);
};

export default AdminTrigger;
